"use client"
import { PHONE_BACKEND_ERROR_MESSAGE } from "@/shared/lib/utils/phone.utils"
import { PrivacyPolicyLink } from "@/shared/components/privacy-policy-link"
import { Button } from "../../../../../shared/components/ui/button"
import { AUTH_PAGE_ROUTE } from "../../../../../shared/constants/pages-routes"
import { useSignupFormStepsContext } from "@/features/auth/context/signup-form-steps-context"
import { useSignupFormContext } from "../../../hooks"
import { useState } from "react"
import { useSession } from "../../../context/session.context"
import { signupUserStep1Complete, sendLoginOtp, VerifyOtp } from "@/features/auth/api/auth.api"
import errorToast from "../../../../../shared/lib/toast/error.toast"
import successToast from "../../../../../shared/lib/toast/success.toast"
import { INVALID_PHONE_ERROR, USER_ALREADY_EXISTS_ERROR } from "../../../constants/backend-errors"
import { Input } from "@/shared/components/ui/new-input"

interface SignUpFormNavigatorProps {
    selectedRole?: 'volunteer' | 'homeowner' | null
    showRoleButtons?: boolean
    onRoleSelected?: (role: 'volunteer' | 'homeowner') => void
}

function SignUpFormNavigator({ 
    selectedRole, 
    showRoleButtons = false, 
    onRoleSelected 
}: SignUpFormNavigatorProps = {}) {
    const { refreshSession } = useSession()
    const {
        currentStep,
        registrationOtpSent,
        setRegistrationOtpSent,
    } = useSignupFormStepsContext()

    const { 
        trigger, 
        getValues, 
        formState: { errors }, 
        setError,
        watch,
    } = useSignupFormContext()

    const [isEarlyRegistrationLoading, setIsEarlyRegistrationLoading] = useState(false)
    const [volunteerOtpLoading, setVolunteerOtpLoading] = useState(false)
    const [volunteerOtpCode, setVolunteerOtpCode] = useState("")
    const email = watch('form1.email')
    const password = watch('form1.password')
    const confirmPassword = watch('form1.confirmPassword')
    const fullname = watch('form1.fullname')
    const phone = watch('form1.phone')
    
    // Get role to determine validation requirements
    const roleToCheck = selectedRole || sessionStorage.getItem('registrationRole') || sessionStorage.getItem('unifiedAuthRole')
    
    // For homeowners, check address fields (address, city, state) AND ensure a place was selected (latitude/longitude must be set)
    // RegisterStateCountryAutocomplete sets values to form1.home.*
    const homeAddress = watch('form1.home.address')
    const homeCity = watch('form1.home.city')
    const homeState = watch('form1.home.state')
    const homeLatitude = watch('form1.home.latitude')
    const homeLongitude = watch('form1.home.longitude')
    
    // Both volunteers and homeowners use OTP login (passwordless)
    // When both role buttons shown (no role yet), use minimal validation so either can proceed
    const baseFieldsValid = !!(fullname?.trim() && phone?.trim() && email?.trim())
    
    // For homeowners, also require address selection from Google suggestions
    // Latitude and longitude are only set when a place is selected, not when typed in
    const addressValid = roleToCheck === 'homeowner'
        ? !!(homeAddress?.trim() && homeCity?.trim() && homeState?.trim() && homeLatitude !== undefined && homeLatitude !== null && homeLongitude !== undefined && homeLongitude !== null)
        : true
    
    const isFormValid = baseFieldsValid && addressValid
    
    // Handle role selection and registration
    const handleRoleRegistration = async (role: 'volunteer' | 'homeowner') => {
        // Store role before submitting
        if (onRoleSelected) {
            onRoleSelected(role)
        }
        sessionStorage.setItem('registrationRole', role)
        // Submit registration with role
        await handleEarlyRegistration(role)
    }
    
    const handleEarlyRegistration = async (userType?: 'volunteer' | 'homeowner') => {
        setIsEarlyRegistrationLoading(true)
        const isValid = await trigger('form1', { shouldFocus: true })
        try {
            if (isValid) {
                const form1 = getValues('form1')
                
                // Validate required fields are present
                if (!form1.fullname || !form1.email || !form1.phone) {
                    errorToast('Please fill in all required fields (Full Name, Email, Phone Number)')
                    return
                }

                // Get role from parameter or sessionStorage
                // Check both registrationRole and unifiedAuthRole for consistency
                // If no role is found, this should not happen (user should have selected role first)
                const role = userType || sessionStorage.getItem('registrationRole') || sessionStorage.getItem('unifiedAuthRole')
                
                if (!role || (role !== 'volunteer' && role !== 'homeowner')) {
                    errorToast('Please select a role first')
                    // Redirect to role selection if no role found
                    window.location.href = '/role'
                    return
                }
                
                // Get team from sessionStorage if available - check both keys
                const team = sessionStorage.getItem('registrationTeam') || sessionStorage.getItem('unifiedAuthTeam')
                
                console.log('[EmailRegistration] Registration data:', {
                    role,
                    team,
                    hasTeam: !!team,
                    registrationTeam: sessionStorage.getItem('registrationTeam'),
                    unifiedAuthTeam: sessionStorage.getItem('unifiedAuthTeam'),
                })
                
                // Prepare form data for submission (age is not part of form anymore)
                // Both volunteers and homeowners use OTP login - no password
                const form1ToSubmit: any = {
                    fullname: String(form1.fullname || ""),
                    email: String(form1.email || ""),
                    phone: form1.phone || "",
                    password: undefined,
                    confirmPassword: undefined,
                    userType: role, // Add userType to the submission
                }
                
                // Add team if available (for volunteers)
                if (team && role === 'volunteer') {
                    form1ToSubmit.team = team
                }
                
                // Add address data for homeowners
                // RegisterStateCountryAutocomplete sets values to form1.home.*
                if (role === 'homeowner') {
                    // The RegisterStateCountryAutocomplete component sets values to form1.home.*
                    const homeData = (form1 as any).home || {}
                    
                    // Extract address data from form1.home.* (where RegisterStateCountryAutocomplete sets it)
                    const address = homeData.address || form1.address
                    const city = homeData.city || form1.city
                    const state = homeData.state || form1.state
                    const zipCode = homeData.zipCode || form1.zipCode
                    const latitude = homeData.latitude !== undefined && homeData.latitude !== null ? homeData.latitude : (form1.latitude !== undefined && form1.latitude !== null ? form1.latitude : undefined)
                    const longitude = homeData.longitude !== undefined && homeData.longitude !== null ? homeData.longitude : (form1.longitude !== undefined && form1.longitude !== null ? form1.longitude : undefined)
                    
                    console.log('[EmailRegistration] Extracted address data:', {
                        hasForm1Address: !!form1.address,
                        hasHomeData: !!homeData.address,
                        address,
                        city,
                        state,
                        zipCode,
                        latitude,
                        longitude,
                        form1Keys: Object.keys(form1),
                        homeDataKeys: Object.keys(homeData),
                        fullForm1: form1,
                    })
                    
                    // Only add address fields if we have the required data
                    if (address && city && state) {
                        form1ToSubmit.address = String(address)
                        form1ToSubmit.city = String(city)
                        form1ToSubmit.state = String(state)
                        if (zipCode) form1ToSubmit.zipCode = String(zipCode)
                        if (latitude !== undefined && latitude !== null) form1ToSubmit.latitude = Number(latitude)
                        if (longitude !== undefined && longitude !== null) form1ToSubmit.longitude = Number(longitude)
                        
                        // Get region from sessionStorage (passed from URL)
                        const region = sessionStorage.getItem('unifiedAuthRegion') || sessionStorage.getItem('registrationRegion')
                        if (region) form1ToSubmit.region = region
                        
                        console.log('[EmailRegistration] Address data to submit:', {
                            address: form1ToSubmit.address,
                            city: form1ToSubmit.city,
                            state: form1ToSubmit.state,
                            zipCode: form1ToSubmit.zipCode,
                            latitude: form1ToSubmit.latitude,
                            longitude: form1ToSubmit.longitude,
                            region: form1ToSubmit.region,
                        })
                    } else {
                        console.warn('[EmailRegistration] Missing required address fields:', {
                            hasAddress: !!address,
                            hasCity: !!city,
                            hasState: !!state,
                        })
                    }
                }
                
                // Ensure phone number is in E.164 format
                if (form1ToSubmit.phone && typeof form1ToSubmit.phone === 'string') {
                    const { transformToE164 } = await import('@/shared/lib/utils/phone.utils')
                    form1ToSubmit.phone = transformToE164(form1ToSubmit.phone)
                }
                
                const signupUserRes = await signupUserStep1Complete(form1ToSubmit)
                if (!signupUserRes.success) throw new Error(signupUserRes.message)
                
                // Both volunteers and homeowners: send OTP and show verification step (passwordless)
                const email = getValues("form1.email")
                const otpRes = await sendLoginOtp(email)
                if (!otpRes.success) throw new Error(otpRes.message || 'Failed to send verification code')
                setRegistrationOtpSent(true)
            }
        } catch (e) {
            const error = e as Error
            switch(error.message) {
                case INVALID_PHONE_ERROR:
                   
                    setError(
                        'form1.phone', 
                        { message: PHONE_BACKEND_ERROR_MESSAGE },  
                        { shouldFocus: true },
                    )
                    break;
                case USER_ALREADY_EXISTS_ERROR:
                    setError(
                        'form1.email', 
                        { message: error.message },  
                        { shouldFocus: true },
                    )
                    break;
                default:
                    errorToast(error.message);
            }
        } finally {
            setIsEarlyRegistrationLoading(false)
        }
    }

    const handleVolunteerOtpVerify = async () => {
        const code = volunteerOtpCode.replace(/\D/g, "").slice(0, 6)
        if (code.length !== 6) {
            errorToast("Please enter the 6-digit code")
            return
        }
        setVolunteerOtpLoading(true)
        try {
            const email = getValues("form1.email")
            const res = await VerifyOtp(email, code)
            if (!res.success) throw new Error(res.message)
            successToast("Sign-up successful! Welcome aboard.")
            await refreshSession()
            window.location.href = "/profile"
        } catch (e) {
            errorToast((e as Error).message)
        } finally {
            setVolunteerOtpLoading(false)
        }
    }

    // OTP verification step (after account created, before redirect)
    if (registrationOtpSent) {
        return (
            <div className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                    We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
                </p>
                <div className="space-y-1.5">
                    <label className="text-sm font-medium">Enter code</label>
                    <Input
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        inputMode="numeric"
                        value={volunteerOtpCode}
                        onChange={(e) => setVolunteerOtpCode(e.target.value.replace(/\D/g, ""))}
                    />
                </div>
                <Button
                    type="button"
                    className="w-full"
                    onClick={handleVolunteerOtpVerify}
                    disabled={volunteerOtpLoading || volunteerOtpCode.length !== 6}
                    showLoader={volunteerOtpLoading}
                >
                    {volunteerOtpLoading ? "Verifying..." : "Verify & log in"}
                </Button>
            </div>
        )
    }

    if (currentStep.number === 1) {
        // Show role selection buttons if requested (matching OAuth flow pattern)
        if (showRoleButtons) {
            return (
                <>
                    <div className="space-y-3">
                        <Button
                            type="button"
                            className="w-full text-wrap text-center align-items-center"
                            onClick={() => handleRoleRegistration('volunteer')}
                            disabled={!isFormValid || isEarlyRegistrationLoading}
                            showLoader={isEarlyRegistrationLoading}
                            variant="default"
                        >
                            {isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Volunteer'}
                        </Button>
                        <Button
                            type="button"
                            className="w-full text-wrap text-center align-items-center"
                            onClick={() => handleRoleRegistration('homeowner')}
                            disabled={!isFormValid || isEarlyRegistrationLoading}
                            showLoader={isEarlyRegistrationLoading}
                            variant="outline"
                        >
                            {isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Homeowner'}
                        </Button>
                    </div>
                    <p className="text-xs text-center text-muted-foreground pt-2">
                        By creating an account, you agree to our{" "}
                        <PrivacyPolicyLink className="underline hover:text-foreground" />.
                    </p>
                </>
            )
        }
        
        // Show single role-specific button if role is already selected
        // If no role is found, show role buttons instead of defaulting
        const roleToUse = selectedRole || sessionStorage.getItem('registrationRole') || sessionStorage.getItem('unifiedAuthRole')
        
        // If no role found, show role selection buttons instead of defaulting
        if (!roleToUse || (roleToUse !== 'volunteer' && roleToUse !== 'homeowner')) {
            return (
                <>
                    <div className="space-y-3">
                        <Button
                            type="button"
                            className="w-full text-wrap text-center align-items-center"
                            onClick={() => handleRoleRegistration('volunteer')}
                            disabled={!isFormValid || isEarlyRegistrationLoading}
                            showLoader={isEarlyRegistrationLoading}
                            variant="default"
                        >
                            {isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Volunteer'}
                        </Button>
                        <Button
                            type="button"
                            className="w-full text-wrap text-center align-items-center"
                            onClick={() => handleRoleRegistration('homeowner')}
                            disabled={!isFormValid || isEarlyRegistrationLoading}
                            showLoader={isEarlyRegistrationLoading}
                            variant="outline"
                        >
                            {isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Homeowner'}
                        </Button>
                    </div>
                    <p className="text-xs text-center text-muted-foreground pt-2">
                        By creating an account, you agree to our{" "}
                        <PrivacyPolicyLink className="underline hover:text-foreground" />.
                    </p>
                </>
            )
        }
        
        const buttonText = roleToUse === 'homeowner' 
            ? (isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Homeowner')
            : (isEarlyRegistrationLoading ? 'Creating Account...' : 'Register as a Volunteer')
        
        return (
            <>
                <Button
                    type="button"
                    onClick={() => handleEarlyRegistration(roleToUse as 'volunteer' | 'homeowner')}
                    disabled={!isFormValid || isEarlyRegistrationLoading}
                    showLoader={isEarlyRegistrationLoading}
                    className="w-full"
                >
                    {buttonText}
                </Button>
                <p className="text-xs text-center text-muted-foreground pt-2">
                    By creating an account, you agree to our{" "}
                    <PrivacyPolicyLink className="underline hover:text-foreground" />.
                </p>
            </>
        )
    }

    return null
}

export default SignUpFormNavigator